import { useState } from "react";
import { Badge, Button, Cluster, IconButton, Input } from "@fwf/fwfa-design-system";
import type { Toasts } from "../hooks";

export type SlugEditorProps = {
  resourceId: string;
  slugs: string[];
  disabled?: boolean;
  onSlugsChanged: (slugs: string[]) => void;
  toasts: Toasts;
};

/**
 * Port slugs are DNS labels on the preview host, so the editor accepts whatever an
 * operator types and reduces it to something routable rather than rejecting it.
 * Anything outside `[a-z0-9._-]` collapses to a single hyphen, and leading or
 * trailing hyphens go: a slug like `-foo-` resolves but reads as a typo.
 */
function normaliseSlug(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Inline slug editor for one instance. This is a field edit rather than a row
 * action, so it POSTs directly instead of going through the shared action runner:
 * there is nothing to confirm, no optimistic row state to revert, and a slug change
 * should not put the row into its busy state or trigger a page reload.
 */
export function SlugEditor({
  resourceId,
  slugs,
  disabled,
  onSlugsChanged,
  toasts,
}: SlugEditorProps) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const locked = busy || disabled === true;

  // The endpoint takes the whole list, not a delta, so add and remove share a path.
  const commit = async (next: string[]) => {
    setBusy(true);
    try {
      const response = await fetch("/api/admin/gcp/instances/slugs", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resourceId, slugs: next }),
      });
      if (!response.ok) {
        toasts.error("Slug update failed", (await response.text()) || undefined);
        return;
      }
      onSlugsChanged(next);
    } catch {
      toasts.error("Slug update failed", "Network error.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setAdding(false);
    setDraft("");
  };

  const add = () => {
    const slug = normaliseSlug(draft);
    cancel();
    // An empty or duplicate entry just closes the field. Neither is a failure the
    // operator needs told about, and neither is worth a round trip.
    if (!slug || slugs.includes(slug)) return;
    void commit([...slugs, slug]);
  };

  return (
    <Cluster>
      {/* Soft, not accent: a slug is a static label, and the DS keeps the
          accent for the one emphasis per view. Two slugs on one row already
          broke the never-two-yellows rule on their own. */}
      {slugs.map((slug) => (
        <Badge key={slug} variant="soft">
          {slug}
          <IconButton
            label={`Remove ${slug}`}
            compact
            disabled={locked}
            onClick={() => void commit(slugs.filter((existing) => existing !== slug))}
          >
            ×
          </IconButton>
        </Badge>
      ))}

      {adding ? (
        <Input
          value={draft}
          autoFocus
          disabled={busy}
          aria-label={`Add a port slug to ${resourceId}`}
          placeholder="slug"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
            if (event.key === "Escape") cancel();
          }}
        />
      ) : (
        <Button variant="ghost" compact disabled={locked} onClick={() => setAdding(true)}>
          + slug
        </Button>
      )}
    </Cluster>
  );
}
